import { useEffect, useState, useCallback } from "react";
import { X, Search, Plus, UserPlus } from "lucide-react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import ConfigDesks from "../../../../../services/configDesks";
import AdLinkServices from "../../../../../services/AdLinkServices";
import { useQueryClient } from "@tanstack/react-query";
import ImageGeneric from "@/assets/images/lg.png";
import { AddNewUserModal } from "./AddNewUserModal";
import { useTranslation } from "react-i18next";

interface User {
  id: number;
  name: string;
  last_name: string;
  email: string;
  filePath: string | null;
  cargo_id?: string;
}

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  deskId: number;
  currentSharedUsers: number[];
  itemType?: "desk" | "reunion";
}

export function AddUserModal({
  isOpen,
  onClose,
  deskId,
  currentSharedUsers,
  itemType = "desk",
}: AddUserModalProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState<any>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
  const itemsPerPage = 10;
  const queryClient = useQueryClient();
  // Debounce do input de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Buscar usuários
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ConfigDesks.getContatosBusca(
        currentPage,
        itemsPerPage,
        debouncedSearch
      );

      if (response.status === 200) {
        setUsers(response.data);
      } else {
        toast.error(t("resumoDesk.adLink.errors.fetch_users"));
      }
    } catch (error) {
      toast.error(t("resumoDesk.adLink.errors.fetch_users"));
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen, fetchUsers]);

  const handleAddUser = (user: User) => {
    confirmAlert({
      title: t("resumoDesk.adLink.add_user"),
      message: t("resumoDesk.adLink.add_user_confirmation", {
        name: `${user.name} ${user.last_name}`,
      }),
      buttons: [
        {
          label: t("common:yes"),
          onClick: async () => {
            setIsAddingUser(true);
            try {
              const updatedUsers = [...currentSharedUsers, user.id];
              const response =
                itemType === "desk"
                  ? await AdLinkServices.updateSharedUsers(deskId, updatedUsers)
                  : await AdLinkServices.updateReunionSharedUsers(
                      deskId,
                      updatedUsers
                    );

              if (response.status === 200 || response.status === 201) {
                toast.success(t("resumoDesk.adLink.user_added_success"));
                queryClient.invalidateQueries({ queryKey: ["dashboardData"] });
                onClose();
              } else {
                toast.error(t("resumoDesk.adLink.errors.add_user"));
              }
            } catch (error) {
              toast.error(t("resumoDesk.adLink.errors.add_user"));
            } finally {
              setIsAddingUser(false);
            }
          },
        },
        {
          label: t("common:cancel"),
          onClick: () => {},
        },
      ],
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= users?.qtdRegistros) {
      setCurrentPage(newPage);
    }
  };

  const handleOpenNewUserModal = () => {
    setIsNewUserModalOpen(true);
  };

  const handleNewUserCreated = async (userId: number) => {
    setIsAddingUser(true);
    try {
      const updatedUsers = [...currentSharedUsers, userId];
      const response =
        itemType === "desk"
          ? await AdLinkServices.updateSharedUsers(deskId, updatedUsers)
          : await AdLinkServices.updateReunionSharedUsers(deskId, updatedUsers);

      if (response.status === 200 || response.status === 201) {
        toast.success(t("resumoDesk.adLink.user_added_to_adlink_success"));
        queryClient.invalidateQueries({ queryKey: ["dashboardData"] });
        setIsNewUserModalOpen(false);
        onClose();
      } else {
        toast.error(t("resumoDesk.adLink.errors.add_user_to_adlink"));
      }
    } catch (error) {
      toast.error(t("resumoDesk.adLink.errors.add_user_to_adlink"));
    } finally {
      setIsAddingUser(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            {t("resumoDesk.adLink.add_user_to_adlink")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isAddingUser}
          >
            <X size={24} />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-6 border-b">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder={t("resumoDesk.adLink.search_placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-400 focus:border-transparent"
              disabled={isAddingUser}
            />
          </div>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg animate-pulse"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                      <div className="h-3 bg-gray-200 rounded w-48"></div>
                    </div>
                  </div>
                  <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                </div>
              ))}
            </div>
          ) : users?.data && users.data.length > 0 ? (
            <div className="space-y-3">
              {users.data.map((user: User) => {
                const isAlreadyShared = currentSharedUsers?.includes(user.id);
                return (
                  <div
                    key={user.id}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-all ${
                      isAlreadyShared
                        ? "border-gray-200 bg-gray-50"
                        : "border-gray-200 hover:border-azul-400 hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={user.filePath ?? ImageGeneric}
                        alt={`${user.name} ${user.last_name}`}
                        className="w-10 h-10 rounded-full object-cover"
                      />

                      <div>
                        <p className="font-medium text-gray-800">
                          {user.name} {user.last_name}
                        </p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    {isAlreadyShared ? (
                      <span className="text-sm text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
                        {t("resumoDesk.adLink.already_added")}
                      </span>
                    ) : (
                      <button
                        onClick={() => handleAddUser(user)}
                        disabled={isAddingUser}
                        className="p-2 bg-azul-400 hover:bg-azul-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t("resumoDesk.adLink.add_user")}
                      >
                        <Plus size={20} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Search size={48} className="mb-4 opacity-50" />
              <p className="mb-4">{t("resumoDesk.adLink.no_users_found")}</p>
              {debouncedSearch && (
                <button
                  onClick={handleOpenNewUserModal}
                  disabled={isAddingUser}
                  className="flex items-center gap-2 px-6 py-3 bg-azul-400 hover:bg-azul-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                >
                  <UserPlus size={20} />
                  {t("resumoDesk.adLink.create_new_user")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {users?.data && users.data.length > 0 && (
          <div className="p-6 border-t flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {t("pagination.page")} {currentPage} {t("pagination.of")}{" "}
              {users.qtdRegistros || 1}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isAddingUser}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t("pagination.previous")}
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === users.qtdRegistros || isAddingUser}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t("pagination.next")}
              </button>
            </div>
          </div>
        )}
      </div>

      <AddNewUserModal
        isOpen={isNewUserModalOpen}
        onClose={() => setIsNewUserModalOpen(false)}
        onUserCreated={handleNewUserCreated}
        initialEmail={debouncedSearch}
      />
    </div>
  );
}
